<script lang="ts">
  import { clickOutside, getHref } from '../../helpers/sections-helper';
  import { BulletPoint } from '../../components/index';
  import { Pin } from '../../components/icons/index';
  import './css/areasWeService.scss';
  import type { AreasWeService } from '../types';
  import { getContext, onMount } from 'svelte';
  import { getClientLocation } from 'core-components';
  import { page } from '$app/stores';
  import { X } from 'icons';
  import { Input, TextArea } from 'core-components';
  import { formatPhoneNumberHref } from '../../helpers/phoneNumberHelper';
  import type { MktSettings } from 'customer-components/lib/types';
  import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks,
  } from 'body-scroll-lock';
  import {
    formatPhoneNumber,
    isValidPhoneNumber,
    isValidEmailAddress,
  } from 'core-components/helpers';
  import {
    MailAltIcon,
    PhoneAltFilledIcon,
    UserIcon,
  } from '../../components/icons';
  import { clickOutsideAction } from 'svelte-legos';
  import { browser } from '$app/environment';

  export let properties: AreasWeService;
  export let settings: MktSettings;

  const hash = getContext<string>('hash');

  let formSubmitted = false,
    name = '',
    email = '',
    phone = '',
    message = '',
    emailError = false,
    phoneError = false,
    messageError = false;

  $: ({ button, callouts, headline, items, kicker, backgroundImage, popup } =
    properties);

  $: ({ phoneNumber } = settings);

  $: selectedCity = properties.selectedCity || null;
  $: formSubmitted =
    typeof properties.formSubmitted === 'boolean'
      ? properties.formSubmitted
      : false;

  function handleSubmit(event: Event) {
    if (!isValidPhoneNumber(phone) && phone.length > 0) {
      phoneError = true;
      return;
    } else if (!isValidEmailAddress(email) && email.length > 0) {
      emailError = true;
      return;
    } else if (message.length < 3) {
      messageError = true;
      return;
    } else if (email || phone) {
      messageError = false;
      emailError = false;
      phoneError = false;
      properties.submitLead(event);
      formSubmitted = true;
    }
  }

  const handlePhoneChange = () => {
    phone = formatPhoneNumber(phone);
  };

  onMount(() => {
    if (items?.length > 0)
      getClientLocation().then(data => {
        const element = items.find(
          item => item.toLocaleLowerCase() === data.city.name.toLowerCase(),
        );
        if (element) {
          items = items.filter(item => item !== element);
          items.unshift(element);
        }
      });
  });

  $: {
    if (browser) {
      if (!popup || !popup.kicker || !popup.headline || !popup.buttonText) {
        // do nothing
      } else {
        if (selectedCity) {
          disableBodyScroll(document.querySelector('body'));
        } else {
          enableBodyScroll(document.querySelector('body'));
          clearAllBodyScrollLocks();
        }
      }
    }
  }

  const handleResetForm = () => {
    selectedCity = null;
    formSubmitted = false;
    name = '';
    email = '';
    phone = '';
    message = '';
  };
</script>

<section class="area-map2">
  <div class="wrapper">
    <div class="area-top">
      <p class="area-title">
        <Pin />
        {@html kicker?.value}
      </p>
      <h2 class="h2">{@html headline?.value}</h2>
      <div class="area-background">
        <img src={backgroundImage} alt="BackgroundImage" loading="lazy" />
      </div>
    </div>
    <div class="area-boxes-holder">
      {#each items as item}
        <button
          class="area-box no-underline"
          on:click={() => (selectedCity = item)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
          >
            <path
              d="M10.5 1.66675C6.82415 1.66675 3.83332 4.65758 3.83332 8.32925C3.80915 13.7001 10.2467 18.1534 10.5 18.3334C10.5 18.3334 17.1908 13.7001 17.1667 8.33342C17.1667 4.65758 14.1758 1.66675 10.5 1.66675ZM10.5 11.6667C8.65832 11.6667 7.16665 10.1751 7.16665 8.33342C7.16665 6.49175 8.65832 5.00008 10.5 5.00008C12.3417 5.00008 13.8333 6.49175 13.8333 8.33342C13.8333 10.1751 12.3417 11.6667 10.5 11.6667Z"
              fill="currentColor"
            />
          </svg>
          <p class="text-small">{item}</p>
        </button>
      {/each}
    </div>

    {#if button?.label?.length}
      <div class="bottom-boxes">
        <a
          id="areas-we-service-book-now"
          class="primary-button open-form-js"
          href={getHref(button.link_type, button.link)}
          >{@html button.label}
        </a>
        <div class="small-text-box">
          {#each callouts.value as item}
            <div class="infline-flex items-center">
              <BulletPoint bulletIcon={callouts?.icon} />
              <p class="text-small">{@html item}</p>
            </div>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</section>

<!-- svelte-ignore empty-block -->
{#if !popup || !popup.kicker || !popup.headline || !popup.buttonText}{:else if selectedCity}
  <div
    class="fixed bottom-0 left-0 right-0 top-0 z-50 flex h-screen max-h-[100svh] w-full flex-col items-center justify-end gap-6 bg-black/40 md:justify-center"
  >
    <div
      use:clickOutsideAction
      on:clickoutside={() => {
        handleResetForm();
      }}
      class="relative flex h-auto min-h-[520px] w-full max-w-[800px] overflow-hidden rounded-lg bg-white"
    >
      <button
        class="absolute right-0 top-0 z-30 p-[16px] text-white md:text-black"
        on:click={() => {
          handleResetForm();
        }}
      >
        <X />
      </button>
      <div
        class="relative flex hidden h-full w-full max-w-[50%] flex-col items-center justify-center gap-4 overflow-hidden md:flex"
      >
        <img
          class="absolute bottom-0 left-0 right-0 top-0 h-full w-full object-cover"
          src={popup.backgroundImage}
          alt="BackgroundImage"
          loading="lazy"
        />
        <div
          style="background:linear-gradient(90deg, rgba(17, 17, 17, 0.96) 25.55%, rgba(17, 17, 17, 0.2) 99.00%);"
          class="absolute bottom-0 left-0 right-0 top-0 h-full w-full opacity-90"
        />

        <div class="z-40 w-full px-[24px]">
          <div
            class="mb-[16px] inline-flex gap-[8px] rounded-lg bg-white p-[8px] text-[14px] font-semibold"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.99999 1.66667C6.32415 1.66667 3.33332 4.65751 3.33332 8.32917C3.30915 13.7 9.74665 18.1533 9.99999 18.3333C9.99999 18.3333 16.6908 13.7 16.6667 8.33334C16.6667 4.65751 13.6758 1.66667 9.99999 1.66667ZM9.99999 11.6667C8.15832 11.6667 6.66665 10.175 6.66665 8.33334C6.66665 6.49167 8.15832 5.00001 9.99999 5.00001C11.8417 5.00001 13.3333 6.49167 13.3333 8.33334C13.3333 10.175 11.8417 11.6667 9.99999 11.6667Z"
                fill="var(--color-accent)"
              />
            </svg>
            {popup.kicker.value?.replace('[city]', selectedCity) || ''}
          </div>
          <div
            class="serivcearea-headline mb-[16px] text-[24px] font-bold text-white"
          >
            {@html popup.headline.value?.replace('[city]', selectedCity) || ''}
          </div>
          <div class="flex gap-[16px]">
            {#each popup.callouts.value as item}
              <div class="inline-flex items-center gap-[4px]">
                <span class="text-[--color-accent]"
                  ><BulletPoint bulletIcon={popup.callouts?.icon} /></span
                >
                <span class="text-[14px] text-white">{@html item}</span>
              </div>
            {/each}
          </div>
        </div>
      </div>
      <div
        class="relative w-full bg-[--color-background] px-[24px] py-[42px] md:max-w-[50%]"
      >
        <img
          class="absolute bottom-0 left-0 top-0 h-full w-full object-cover md:hidden"
          src={popup.backgroundImage}
          alt="BackgroundImage"
          loading="lazy"
        />
        <div
          style="background:linear-gradient(90deg, rgba(17, 17, 17, 0.96) 25.55%, rgba(17, 17, 17, 0.2) 99.00%);"
          class="absolute bottom-0 left-0 top-0 h-full w-full opacity-90 md:hidden"
        ></div>
        {#if formSubmitted}
          <div
            class="relative flex h-full flex-col items-center justify-center gap-[20px] text-center text-white md:text-black"
          >
            <svg
              width="60"
              height="60"
              viewBox="0 0 60 60"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="30" cy="30" r="25" fill="var(--color-accent-text)" />
              <path
                d="M30 5C16.215 5 5 16.215 5 30C5 43.785 16.215 55 30 55C43.785 55 55 43.785 55 30C55 16.215 43.785 5 30 5ZM25.0025 41.0325L15.72 31.77L19.25 28.23L24.9975 33.9675L38.2325 20.7325L41.7675 24.2675L25.0025 41.0325Z"
                fill="var(--color-accent)"
              />
            </svg>

            <b class="text-[24px] font-bold"
              >{@html popup.confirmation?.headline?.value}</b
            >
            <p>
              {@html popup.confirmation?.description?.value}
            </p>
            <p>
              <a
                class="flex items-center gap-[12px] font-medium"
                href="tel:{formatPhoneNumberHref(phoneNumber?.value)}"
              >
                <svg
                  width="21"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.2558 10.2441C15.1785 10.1667 15.0866 10.1052 14.9855 10.0633C14.8844 10.0214 14.7761 9.9998 14.6666 9.9998C14.5572 9.9998 14.4488 10.0214 14.3477 10.0633C14.2466 10.1052 14.1548 10.1667 14.0775 10.2441L12.7491 11.5725C12.1333 11.3891 10.9841 10.9725 10.2558 10.2441C9.52745 9.5158 9.11079 8.36663 8.92745 7.7508L10.2558 6.42246C10.3332 6.34514 10.3947 6.2533 10.4366 6.15221C10.4785 6.05111 10.5001 5.94274 10.5001 5.8333C10.5001 5.72385 10.4785 5.61548 10.4366 5.51439C10.3947 5.41329 10.3332 5.32145 10.2558 5.24413L6.92245 1.9108C6.84513 1.83334 6.75329 1.77189 6.6522 1.72997C6.5511 1.68804 6.44273 1.66646 6.33329 1.66646C6.22384 1.66646 6.11547 1.68804 6.01438 1.72997C5.91328 1.77189 5.82144 1.83334 5.74412 1.9108L3.48412 4.1708C3.16745 4.48746 2.98912 4.92246 2.99579 5.36663C3.01495 6.5533 3.32912 10.675 6.57745 13.9233C9.82579 17.1716 13.9475 17.485 15.135 17.505H15.1583C15.5983 17.505 16.0141 17.3316 16.3291 17.0166L18.5891 14.7566C18.6666 14.6793 18.728 14.5875 18.7699 14.4864C18.8119 14.3853 18.8335 14.2769 18.8335 14.1675C18.8335 14.058 18.8119 13.9496 18.7699 13.8486C18.728 13.7475 18.6666 13.6556 18.5891 13.5783L15.2558 10.2441ZM15.15 15.8375C14.11 15.82 10.5516 15.5408 7.75579 12.7441C4.95079 9.93913 4.67912 6.3683 4.66245 5.34913L6.33329 3.6783L8.48829 5.8333L7.41079 6.9108C7.31284 7.00867 7.24082 7.12941 7.20125 7.2621C7.16167 7.3948 7.15579 7.53526 7.18412 7.6708C7.20412 7.76663 7.69329 10.0391 9.07662 11.4225C10.46 12.8058 12.7325 13.295 12.8283 13.315C12.9637 13.3441 13.1043 13.3387 13.2372 13.2992C13.37 13.2597 13.4907 13.1875 13.5883 13.0891L14.6666 12.0116L16.8216 14.1666L15.15 15.8375Z"
                    fill="currentColor"
                  />
                </svg>
                Call Us {@html phoneNumber?.value}
              </a>
            </p>
          </div>
        {:else}
          <div
            class="relative mb-[16px] inline-flex gap-[8px] rounded-lg bg-white p-[8px] text-[14px] font-semibold md:hidden"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M9.99999 1.66667C6.32415 1.66667 3.33332 4.65751 3.33332 8.32917C3.30915 13.7 9.74665 18.1533 9.99999 18.3333C9.99999 18.3333 16.6908 13.7 16.6667 8.33334C16.6667 4.65751 13.6758 1.66667 9.99999 1.66667ZM9.99999 11.6667C8.15832 11.6667 6.66665 10.175 6.66665 8.33334C6.66665 6.49167 8.15832 5.00001 9.99999 5.00001C11.8417 5.00001 13.3333 6.49167 13.3333 8.33334C13.3333 10.175 11.8417 11.6667 9.99999 11.6667Z"
                fill="var(--color-accent)"
              />
            </svg>
            {popup.kicker.value?.replace('[city]', selectedCity) || ''}
          </div>
          <form
            class="quote-form-inputs relative z-20"
            style="--destructive-foreground:#c00;"
            on:submit|preventDefault={handleSubmit}
          >
            <input type="hidden" name="hash" value={hash} />
            <input type="hidden" name="form_type" value="city-form" />
            <label
              for="name"
              class="text-[16px] font-semibold text-white md:text-black"
              >Name</label
            >
            <div class="fullname-and-email">
              <div class="input-box">
                <Input
                  bind:value={name}
                  placeholder="Name"
                  name="fullName"
                  size="2xlarge"
                  id="fullName"
                >
                  <UserIcon slot="icon" />
                </Input>
              </div>
              <label
                for="email"
                class="mt-[16px] text-[16px] font-semibold text-white md:text-black"
                >Email</label
              >
              <div class="input-box">
                <Input
                  bind:value={email}
                  type="email"
                  placeholder="Email"
                  name="email"
                  id="email"
                  size="2xlarge"
                  error={emailError}
                  errorMessage="Please provide a valid email."
                >
                  <MailAltIcon slot="icon" />
                </Input>
              </div>
            </div>
            <label
              for="phone-number"
              class="mt-[16px] text-[16px] font-semibold text-white md:text-black"
              >Phone Number</label
            >
            <div class="input-box">
              <Input
                bind:value={phone}
                on:input={handlePhoneChange}
                id="phone-number"
                type="tel"
                placeholder="Phone Number"
                name="phone"
                error={phoneError}
                size="2xlarge"
                errorMessage="Please provide a valid phone number"
                rightLabel="Optional"
              >
                <PhoneAltFilledIcon slot="icon" />
              </Input>
            </div>
            <div>
              <div>
                <label
                  for="comments"
                  class="mt-[16px] text-[16px] font-semibold text-white md:text-black"
                  >Your Message</label
                >
              </div>
              <div>
                <TextArea
                  size="giant"
                  extend={true}
                  name="comments"
                  bind:value={message}
                  resizable={false}
                  placeholder="Additional Details (optional)"
                ></TextArea>
              </div>
            </div>
            {#if messageError}
              <p class="text-md rounded-md bg-red-500 !p-4 text-white">
                Please provide us some details so we can provide you with a
                quote
              </p>
            {/if}
            <button
              id="submit-lead-button"
              class="mt-[12px] w-full rounded-[4px] bg-[--color-accent] py-[12px] text-[16px] font-bold text-white"
              type="submit">{@html popup.buttonText}</button
            >
          </form>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  :global(.serivcearea-headline b) {
    color: var(--color-accent);
  }
</style>
